import React, { useEffect, useMemo, useState } from 'react';
import './PromptPerformanceMatrix.scss';
import { useTranslation } from 'react-i18next';
import type { InsightsBrandFunnelsResponse } from '@brand24-original/llm-types';
import { AllBrandsStatsResponse } from '../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { Skeleton } from 'primereact/skeleton';
import BrandIcon from '../../../../shared/components/brand-icon/BrandIcon';
import MatrixHeatmapTable, {
  HeatmapColumn,
  HeatmapRow,
} from '../../../../shared/components/matrix-heatmap/MatrixHeatmapTable';
import { brandColorOf } from '../../../../shared/components/report/brandColors';
import {
  InsightsPromptFunnelsParams,
  useGetInsightsPromptFunnels,
} from '../../../../shared/api/hooks/insights/useGetInsightsPromptFunnels';
import { percentOf } from '../../helpers/overviewInsightHelpers';
import { useBrandSelection } from '../../hooks/useBrandSelection';
import BrandPicker from '../brand-picker/BrandPicker';
import MetricSwitcher from '../../../../shared/components/metric-switcher/MetricSwitcher';

const METRICS = [
  { key: 'visible', labelKey: 'insightPage.promptMatrix.metricVisibility' },
  { key: 'cited', labelKey: 'insightPage.promptMatrix.metricCited' },
  { key: 'top3', labelKey: 'insightPage.promptMatrix.metricTop3' },
  { key: 'top1', labelKey: 'insightPage.promptMatrix.metricTop1' },
] as const;

type MetricKey = (typeof METRICS)[number]['key'];

const DEFAULT_ROWS = 10;

/** Scope zapytania bez paginacji — first/rows to lokalny stan sekcji, doklejany tutaj. */
export type PromptMatrixScope = Omit<InsightsPromptFunnelsParams, 'page' | 'rows'>;

interface PromptPerformanceMatrixProps {
  scope: PromptMatrixScope | null;
  /** Słownik marek (etykiety, focusBrandKey) + baza pickera — payload brand-funnels tej strony. */
  brandFunnels: InsightsBrandFunnelsResponse | null;
  sovBrands: AllBrandsStatsResponse[] | null;
  /** Ładowanie zależności sekcji (brand-funnels + SoV + monitory) — guard jak w FunnelVsCompetitors. */
  isAuxLoading: boolean;
  isAuxError?: boolean;
}

const PromptPerformanceMatrix: React.FC<PromptPerformanceMatrixProps> = ({
  scope,
  brandFunnels,
  sovBrands,
  isAuxLoading,
  isAuxError,
}) => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<MetricKey>('visible');
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const { focusBrandKey, pickerBrands, selectedKeys, selectedBrands, brandColors, selectBrands } =
    useBrandSelection(brandFunnels, sovBrands);

  // Zmiana scope'u (zakres, modele, grupa promptów, marka) wraca na pierwszą stronę — bez tego można
  // "wisieć" na stronie 4 z 2 istniejących po zawężeniu filtra.
  const scopeKey = scope
    ? [
        scope.projectId,
        scope.chatType,
        scope.from?.getTime(),
        scope.to?.getTime(),
        scope.monitorPromptIds?.join(','),
        scope.filterChatTypes?.join(','),
        scope.brand,
      ].join('|')
    : '';
  useEffect(() => {
    setFirst(0);
  }, [scopeKey]);

  const {
    insightsPromptFunnels: data,
    isLoading: isMatrixLoading,
    isPlaceholderData: isPageLoading,
    isError: isMatrixError,
  } = useGetInsightsPromptFunnels(scope ? { ...scope, page: first / rows, rows } : null);

  const isLoading = isAuxLoading || isMatrixLoading;
  const isError = isAuxError || isMatrixError;

  const heatmapColumns: HeatmapColumn[] = useMemo(
    () =>
      selectedBrands.map((brand, index) => {
        const isFocus = brand.brandKey === focusBrandKey;
        return {
          key: brand.brandKey,
          emphasized: isFocus,
          header: (
            <span className="prompt-performance-matrix__brand-header">
              <BrandIcon
                name={brand.label}
                color={brandColorOf(brandColors, brand.label, isFocus, index)}
                size="xs"
              />
              {brand.label}
            </span>
          ),
        };
      }),
    [selectedBrands, focusBrandKey, brandColors]
  );

  // Kolejność i teksty promptów przychodzą z backendu (ORDER BY prompt ASC + paginacja) —
  // FE niczego nie sortuje ani nie mapuje.
  const heatmapRows: HeatmapRow[] = useMemo(() => {
    if (!data) return [];
    return data.rows.map((row) => {
      const cells = new Map(row.brands.map((cell) => [cell.brandKey, cell]));
      return {
        key: String(row.monitorId),
        header: <span className="prompt-performance-matrix__prompt">{row.prompt}</span>,
        // `cited` is null when the brand's domains are unknown — the heatmap draws a dash for null,
        // which must not collapse into a misleading 0%.
        values: selectedBrands.map((brand) => {
          const value = cells.get(brand.brandKey)?.[metric];
          return value === null ? null : percentOf(value ?? 0, row.total);
        }),
      };
    });
  }, [data, selectedBrands, metric]);

  const hasData = !!data && data.totalPrompts > 0 && selectedBrands.length > 0;

  return (
    <div className="stats__panel prompt-performance-matrix">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.promptMatrix.title')}
          <CustomTooltip tooltipText="insightPage.promptMatrix.tooltip" addMargin={false} />
        </div>
        {!isLoading && !isError && hasData && (
          <div className="prompt-performance-matrix__controls">
            <BrandPicker
              brands={pickerBrands}
              focusBrandKey={focusBrandKey}
              selectedKeys={selectedKeys}
              brandColors={brandColors}
              onChange={selectBrands}
            />
            <MetricSwitcher options={METRICS} value={metric} onChange={setMetric} />
          </div>
        )}
      </div>
      {/* The table brings its own frame (matrix-heatmap__frame), so it sits directly under the title
          row like the overview tables — the bordered card is only for the empty/error states.
          A page change keeps the previous payload (so the controls above stay mounted) and only the
          table gives way to the shared table skeleton (row bars) — the same pattern, and the same
          skeleton, as the /sources domain ranking. */}
      {isLoading || isPageLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : isError ? (
        <div className="prompt-performance-matrix__card">
          <EmptyPanel textKey="somethingWentWrong" />
        </div>
      ) : !hasData ? (
        <div className="prompt-performance-matrix__card">
          <EmptyPanel />
        </div>
      ) : (
        <MatrixHeatmapTable
          rowHeader={t('insightPage.promptMatrix.promptHeader')}
          columns={heatmapColumns}
          rows={heatmapRows}
          scrollable
          stackOnMobile
          pagination={{
            first,
            rows,
            totalRecords: data.totalPrompts,
            onPageChange: (event) => {
              setFirst(event.first);
              setRows(event.rows);
            },
          }}
        />
      )}
    </div>
  );
};

export default PromptPerformanceMatrix;
